import * as React from 'react';
import { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Link,
    useTheme,
    TextField,
    Button
} from '@mui/material';
import axios from 'axios';
import toast from 'react-hot-toast';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TaskAltOutlined } from '@mui/icons-material';

export default function Demo({ siteSettings = {} }) {
    const theme = useTheme();
    const [submitted, setSubmitted] = useState(false);
    const demoEmail = siteSettings.demo_contact_email || 'admin@gbinternational.sg';
    const demoPhone = siteSettings.demo_contact_phone || '+91-9940159181';
    const [loading, setLoading] = useState(false);

    const cyan500 = '#06B6D4';
    const blue600 = '#2563EB';
    const cyan100 = '#ECFEFF';

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        requirement: ""
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.name.trim()) newErrors.name = "Full name is required.";
        if (!formData.email.trim()) newErrors.email = "Work email is required.";
        else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Invalid email format.";
        if (!formData.phone.trim()) newErrors.phone = "Phone number is required.";
        if (!formData.company.trim()) newErrors.company = "Company name is required.";
        if (!formData.requirement.trim()) newErrors.requirement = "Requirement is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fill in all required fields correctly.");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/send-email`, formData);

            toast.success("Demo request sent successfully!");
            setSubmitted(true);
            setFormData({ name: "", email: "", phone: "", company: "", requirement: "" });
            setTimeout(() => setSubmitted(false), 5000);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to send demo request.");
        } finally {
            setLoading(false);
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '& fieldset': {
                borderColor: theme.palette.grey[300],
                borderWidth: 2,
            },
            '&.Mui-focused fieldset': {
                borderColor: blue600,
            },
        },
    };
    const gradientText = {
        background: 'linear-gradient(90deg, #06B6D4 0%, #3B82F6 50%, #6366F1 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    };

    const AddUserLabel = ({ children, isRequired = false }) => (
        <Typography
            variant='h2'
            sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                mb: 0.5,
                '&::after': isRequired ? {
                    content: '" *"',
                    color: '#ff0000'
                } : 'none'
            }}
        >
            {children}
        </Typography>
    );

    const TickBox = () => (
        <Box
            sx={{
                width: 45,
                height: 45,
                borderRadius: 1.5,
                backgroundColor: '#17aacfff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >
            <TaskAltOutlined sx={{
                color: '#fff',
                fontSize: 28,
                '& path': {
                    fill: '#fff'
                }
            }} />
        </Box>
    );

    const IconBox = ({ children }) => (
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                flexShrink: 0
            }}
        >
            {children}
        </Box>
    );

    if (submitted) {
        return (
            <Box id="demo" sx={{ py: { xs: 8, md: 12 }, px: { xs: 4, md: 8 }, backgroundColor: '#fff' }}>
                <Container maxWidth="xl">
                    <Grid container spacing={{ xs: 8, lg: 12 }} alignItems="center">
                        <Grid item xs={12} lg={6}>
                            <Typography
                                sx={{
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    mb: 2,
                                    ...gradientText,
                                }}
                            >
                                See It In Action
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: '2.2rem', md: '2.8rem' },
                                    fontWeight: 700,
                                    mb: 2,
                                }}
                            >
                                Want to See It{' '}
                                <Box component="span" sx={gradientText}>
                                    in Action?
                                </Box>
                            </Typography>

                            <Typography sx={{ color: theme.palette.grey[600], mb: 4 }}>
                                Request a personalized demo and discover how NC Pro can
                                transform your quality management process.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {[
                                    {
                                        title: 'Live Demonstration',
                                        desc: 'See NC Pro in action with real-world scenarios',
                                    },
                                    {
                                        title: 'Custom Solutions',
                                        desc: 'Tailored to your specific industry needs',
                                    },
                                    {
                                        title: 'Expert Consultation',
                                        desc: 'Get answers to all your questions',
                                    },
                                ].map((item, i) => (
                                    <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                        <TickBox />
                                        <Box>
                                            <Typography fontWeight={600}>{item.title}</Typography>
                                            <Typography sx={{ color: theme.palette.grey[600] }}>
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Box
                                sx={{
                                    mt: 5,
                                    p: 3,
                                    borderRadius: 2,
                                    backgroundColor: cyan100,
                                }}
                            >
                                <Typography fontWeight={700} mb={2}>
                                    Contact Us Directly
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <IconBox>
                                        <MailOutlineIcon sx={{ fontSize: 18, color: blue600 }} />
                                    </IconBox>
                                    <Link href={`mailto:${demoEmail}`} underline="none" sx={{ color: "black" }}>
                                        {demoEmail}
                                    </Link>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconBox>
                                        <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: blue600 }} />
                                    </IconBox>
                                    <Link href={`tel:${demoPhone.replace(/[^+\d]/g, '')}`} underline="none" sx={{ color: "black" }}>
                                        {demoPhone}
                                    </Link>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: { xs: 6, sm: 8 },
                                    px: { xs: 2, sm: 4 },
                                }}
                            >

                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,

                                        background: `linear-gradient(to bottom right, #0891B2, '#EC4899')`,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3,
                                        boxShadow: theme.shadows[8],
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            color: 'blue',
                                        }}
                                    />
                                </Box>

                                <Typography
                                    component="h3"
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: theme.palette.grey[900],
                                        mb: 1.5,
                                    }}
                                >
                                    Demo Request Sent Successfully!
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme.palette.grey[600],
                                        fontSize: '1.125rem',
                                    }}
                                >
                                    We've received your request and a specialist will contact you shortly to schedule your demonstration.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        );
    }

    return (
        <Box id="demo" sx={{ py: { xs: 8, md: 12 }, px: { xs: 4, md: 8 }, backgroundColor: '#fff' }}>
            <Container maxWidth="xl">
                <Grid container spacing={{ xs: 8, lg: 12 }} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Typography
                            sx={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                mb: 2,
                                ...gradientText,
                            }}
                        >
                            See It In Action
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: '2.2rem', md: '2.8rem' },
                                fontWeight: 700,
                                mb: 2,
                            }}
                        >
                            Want to See It{' '}
                            <Box component="span" sx={gradientText}>
                                in Action?
                            </Box>
                        </Typography>

                        <Typography sx={{ color: theme.palette.grey[600], mb: 4 }}>
                            Request a personalized demo and discover how NC Pro can
                            transform your quality management process.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {[
                                {
                                    title: 'Live Demonstration',
                                    desc: 'See NC Pro in action with real-world scenarios',
                                },
                                {
                                    title: 'Custom Solutions',
                                    desc: 'Tailored to your specific industry needs',
                                },
                                {
                                    title: 'Expert Consultation',
                                    desc: 'Get answers to all your questions',
                                },
                            ].map((item, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                    <TickBox />
                                    <Box>
                                        <Typography fontWeight={600}>{item.title}</Typography>
                                        <Typography sx={{ color: theme.palette.grey[600] }}>
                                            {item.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                mt: 5,
                                p: 3,
                                borderRadius: 3,
                                backgroundColor: cyan100,
                                borderColor: '#389ca1'
                            }}
                        >
                            <Typography fontWeight={700} mb={2}>
                                Contact Us Directly
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <IconBox>
                                    <MailOutlineIcon sx={{ fontSize: 18, color: blue600 }} />
                                </IconBox>
                                <Link href={`mailto:${demoEmail}`} underline="none" sx={{ color: "black" }}>
                                    {demoEmail}
                                </Link>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconBox>
                                    <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: blue600 }} />
                                </IconBox>
                                <Link href={`tel:${demoPhone.replace(/[^+\d]/g, '')}`} underline="none" sx={{ color: "black" }}>
                                    {demoPhone}
                                </Link>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <Box
                            sx={{
                                position: 'relative',
                                backgroundColor: '#ffffff',
                                borderRadius: 3,
                                p: 4,
                                zIndex: 2,
                                overflow: 'hidden',
                                boxShadow: `0 25px 60px rgba(14, 165, 233, 0.25),0 10px 26px rgba(14, 165, 233, 0.18)`,
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundColor: '#fff',
                                    zIndex: -1,
                                }}
                            />
                            <Typography variant="h5" fontWeight={700} mb={1}>
                                Request a Demo
                            </Typography>
                            <Typography sx={{ color: theme.palette.grey[600], mb: 4 }}>
                                Fill in your details and we'll get back to you
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                            >
                                <Box>
                                    <AddUserLabel isRequired={true}>Full Name</AddUserLabel>
                                    <TextField
                                        name="name"
                                        placeholder="Enter Your Name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        sx={textFieldSx}
                                        fullWidth
                                        error={!!errors.name}
                                        helperText={errors.name}
                                    />
                                </Box>

                                <Box>
                                    <AddUserLabel isRequired={true}>Work Email</AddUserLabel>
                                    <TextField
                                        name="email"
                                        placeholder="Enter Your Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        sx={textFieldSx}
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Box>

                                <Box>
                                    <AddUserLabel isRequired={true}>Phone Number</AddUserLabel>
                                    <TextField
                                        name="phone"
                                        placeholder="Enter Your Phone Number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        sx={textFieldSx}
                                        fullWidth
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        inputProps={{
                                            maxLength: 15,
                                        }}
                                        onKeyDown={(e) => {
                                            const allowedKeys = ['+', '(', ')', '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                                                'Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight',
                                                'Home', 'End'];

                                            if (!allowedKeys.includes(e.key) &&
                                                !((e.key >= '0' && e.key <= '9') || e.key === '+' || e.key === '(' || e.key === ')')) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onPaste={(e) => {
                                            const pastedData = e.clipboardData.getData('text');
                                            const sanitized = pastedData.replace(/[^0-9.]/g, '');
                                            const parts = sanitized.split('.');
                                            const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
                                            if (pastedData !== cleanValue) {
                                                e.preventDefault();
                                            }
                                        }}
                                        inputMode="tel"
                                    />
                                </Box>

                                <Box>
                                    <AddUserLabel isRequired={true}>Company Name</AddUserLabel>
                                    <TextField
                                        name="company"
                                        placeholder="Enter Your Company Name"
                                        value={formData.company}
                                        onChange={handleChange}
                                        sx={textFieldSx}
                                        fullWidth
                                        error={!!errors.company}
                                        helperText={errors.company}
                                    />
                                </Box>

                                <Box>
                                    <AddUserLabel isRequired={true}>Requirement</AddUserLabel>
                                    <TextField
                                        name="requirement"
                                        placeholder="Tell us about your quality management needs..."
                                        value={formData.requirement}
                                        onChange={handleChange}
                                        multiline
                                        rows={4}
                                        sx={textFieldSx}
                                        fullWidth
                                        error={!!errors.requirement}
                                        helperText={errors.requirement}
                                    />
                                </Box>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    sx={{
                                        background: `linear-gradient(to right, ${cyan500}, ${blue600})`,
                                        color: '#fff',
                                        py: 1.5,
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: `linear-gradient(to right, #0891B2, #1D4ED8)`,
                                        },
                                        '&:disabled': {
                                            background: theme.palette.grey[400],
                                            cursor: 'not-allowed'
                                        }
                                    }}
                                >
                                    {loading ? "Sending..." : "Request Demo"}
                                    <SendIcon sx={{ ml: 1 }} />
                                </Button>

                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        textAlign: 'center',
                                        color: theme.palette.grey[500],
                                    }}
                                >
                                    By submitting this form, you agree to our privacy policy
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
}
